import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AnimatedCounter from '../components/AnimatedCounter';
import NeuralNetwork from '../components/NeuralNetwork';
import FAQSection from '../components/FAQSection';

function Home() {
  return (
    <div className="page-wrapper">
      <Helmet>
        <title>Bank Pricing Optimisation Software | Clariti AI</title>
        <meta name="description" content="Clariti AI is bank pricing optimisation software for mortgage and savings teams. Model pricing scenarios, improve margins, and make faster rate decisions with data-driven analytics." />
        <link rel="canonical" href="https://www.clariti.ai/" />
        <meta property="og:title" content="Bank Pricing Optimisation Software | Clariti AI" />
        <meta property="og:description" content="Data-driven pricing software for banks. Optimise mortgage and savings rates with scenario modelling and portfolio analytics." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.clariti.ai/" />
        <meta property="og:image" content="https://www.clariti.ai/images/logos/logo_brain_white_bg.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Bank Pricing Optimisation Software | Clariti AI" />
        <meta name="twitter:description" content="Mortgage and savings pricing optimisation software for global banking teams." />
        <meta name="twitter:image" content="https://www.clariti.ai/images/logos/logo_brain_white_bg.png" />
      </Helmet>
      {/* Hero Section */}
      <section className="hero">
        <NeuralNetwork className="nn-layer" nodeCount={40} interactive={true} />
        <div className="container">
          <div className="hero-inner">
            <div className="hero-content">
              <h1>Smarter pricing for mortgage and savings teams</h1>
              <p className="hero-subtitle">
                Clariti AI gives banks a pricing optimisation platform to set smarter
                mortgage and savings rates. Model trade-offs, forecast outcomes, and
                turn pricing complexity into a global competitive advantage.
              </p>
              <div className="hero-buttons">
                <Link to="/contact" className="btn btn-primary">Book a Demo</Link>
                <Link to="/products" className="btn btn-secondary">Explore Products</Link>
              </div>
            </div>
            <div className="hero-media">
              <div className="screenshot-mockup">
                <div className="screenshot-mockup-header">
                  <span className="screenshot-mockup-dot"></span>
                  <span className="screenshot-mockup-dot"></span>
                  <span className="screenshot-mockup-dot"></span>
                </div>
                <img 
                  src="/images/screenshots/deposits/deposits overiew page.png" 
                  alt="Clariti AI dashboard overview" 
                  className="screenshot-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar with Animated Counters */}
      <section className="trust-bar">
        <div className="container">
          <div className="trust-bar-inner">
            <div className="trust-stat">
              <div className="trust-stat-number">
                <CurrencyCounter value={300} />
              </div>
              <div className="trust-stat-label">Balances under optimisation</div>
            </div>
            <div className="trust-stat">
              <div className="trust-stat-number">
                <AnimatedCounter end={5} duration={1500} suffix="bp-15bp" />
              </div>
              <div className="trust-stat-label">Typical margin uplift</div>
            </div>
            <div className="trust-stat">
              <div className="trust-stat-number">
                <AnimatedCounter end={50} duration={1800} suffix="-" />
                <AnimatedCounter end={250} duration={1800} suffix="×" />
              </div>
              <div className="trust-stat-label">ROI</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Pricing Optimisation */}
      <section className="section">
        <NeuralNetwork className="nn-layer" nodeCount={20} interactive={false} />
        <div className="container">
          <div className="section-header">
            <h2>Precision pricing. Powered by data.</h2>
            <p>
              Pricing involves hundreds of moving parts. We cut through the complexity, 
              giving you full visibility into every trade-off so you can focus on what 
              actually drives results.
            </p>
          </div>
          <div className="why-grid">
            <div className="why-card">
              <div className="why-icon">
                <AnalyticsIcon />
              </div>
              <div className="why-card-text">
                <h3>Replace manual workflows with scalable analytics</h3>
                <p>
                  Move beyond spreadsheets to a platform that updates in real time, 
                  keeping your pricing aligned with market conditions as they evolve.
                </p>
              </div>
            </div>
            <div className="why-card">
              <div className="why-icon">
                <InsightIcon />
              </div>
              <div className="why-card-text">
                <h3>Turn market data into actionable insight</h3>
                <p>
                  Go beyond observing competitor moves. Understand what they mean for 
                  your portfolio, your margins, and your competitive positioning.
                </p>
              </div>
            </div>
            <div className="why-card">
              <div className="why-icon">
                <StreamlineIcon />
              </div>
              <div className="why-card-text">
                <h3>See the impact before you act</h3>
                <p>
                  Use your portfolio data to model exactly what happens when rates 
                  change — the effect on volumes, margins, and your bottom line — so 
                  every pricing decision is informed, not intuitive.
                </p>
              </div>
            </div>
          </div>
          <p className="why-closing">
            Proactive, data-driven pricing is the foundation of sustained competitive advantage.
          </p>
        </div>
      </section>

      {/* Products Overview */}
      <section className="section section-light">
        <NeuralNetwork className="nn-layer" nodeCount={18} interactive={false} />
        <div className="container">
          <div className="section-header">
            <h2>Our Solutions</h2>
          </div>
          <div className="products-grid">
            <div className="product-card">
              <div className="product-icon">
                <HomeIcon />
              </div>
              <h3>Mortgage Rate Optimisation</h3>
              <p>
                Set optimal mortgage rates that balance volume, margin, and risk. 
                Built for pricing teams who need clarity in a complex market.
              </p>
              <Link to="/products/mortgage" className="product-link">
                Learn more <ArrowIcon />
              </Link>
            </div>
            <div className="product-card">
              <div className="product-icon">
                <PiggyBankIcon />
              </div>
              <h3>Savings Rate Optimisation</h3>
              <p>
                Price deposits strategically to manage funding costs and customer 
                retention. Data-driven insights for treasury and savings teams.
              </p>
              <Link to="/products/savings" className="product-link">
                Learn more <ArrowIcon />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Credibility Section */}
      <section className="section credibility">
        <div className="container">
          <h2>Trusted by banks across the UK and Europe</h2>
          <QuoteCarousel />
          <p className="credibility-note">
            We work with banks of all sizes — from recent new entrants to established mutuals and major banks. References available on request.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <FAQSection faqs={[
        {
          question: 'What is Clariti AI?',
          answer: 'Clariti AI is a pricing optimisation platform built for banks. We help mortgage and savings teams set smarter rates by modelling trade-offs between volume, margin, and risk — replacing spreadsheet-based pricing with real-time, data-driven analytics.'
        },
        {
          question: 'Who is Clariti AI designed for?',
          answer: 'Our platform is built for pricing, product, and treasury teams at banks and building societies. Whether you manage mortgage rates, savings products, or both, Clariti AI gives you the tools to make faster, more confident pricing decisions.'
        },
        {
          question: 'How does pricing optimisation work?',
          answer: 'We combine real-time competitive intelligence, customer behavioural models, and portfolio-level optimisation. This means you can see exactly how a rate change affects volumes, margins, and cannibalisation across your product range — before you make the change.'
        },
        {
          question: 'How long does implementation take?',
          answer: 'Most banks are live within 4–8 weeks. We handle the heavy lifting — data integration, model calibration, and user onboarding — so your team can start seeing value quickly without a lengthy IT project.'
        },
        {
          question: 'Is my data secure?',
          answer: 'Absolutely. We operate within enterprise-grade security frameworks, including encryption at rest and in transit, role-based access controls, and compliance with UK and European data protection standards. We work with your IT and compliance teams to meet your specific requirements.'
        },
        {
          question: 'What kind of results can I expect?',
          answer: 'Our clients typically see a margin uplift of 5–15 basis points while maintaining or growing balances. The platform also significantly reduces the time spent on manual pricing analysis, freeing your team to focus on strategic decisions rather than data wrangling.'
        },
      ]} />

      {/* Final CTA */}
      <section className="cta-section">
        <NeuralNetwork className="nn-layer" nodeCount={30} interactive={true} />
        <div className="container">
          <div className="cta-content">
            <h2>Ready to optimise your pricing?</h2>
            <p>
              See how Clariti AI can help your team make smarter, faster pricing decisions.
            </p>
            <div className="cta-button-group">
              <Link to="/contact" className="btn btn-white">Book a Demo</Link>
              <Link to="/insights" className="btn btn-white">Read Insights</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// Currency Counter Component (Simplified to GBP only)
function CurrencyCounter({ value }) {
  return (
    <>
      $
      <AnimatedCounter end={value} duration={2000} />
      bn+
    </>
  );
}

// Icons
function AnalyticsIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"></rect>
      <line x1="8" y1="16" x2="8" y2="12"></line>
      <line x1="12" y1="16" x2="12" y2="8"></line>
      <line x1="16" y1="16" x2="16" y2="10"></line>
      <polyline points="7 10 12 6 16 8" strokeWidth="1.5"></polyline>
    </svg>
  );
}

function InsightIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"></circle>
      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
      <polyline points="8 11 10 13 14 9"></polyline>
    </svg>
  );
}

function StreamlineIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="5" cy="6" r="2"></circle>
      <circle cx="12" cy="12" r="2"></circle>
      <circle cx="19" cy="6" r="2"></circle>
      <path d="M7 6h10"></path>
      <path d="M5 8v2a2 2 0 0 0 2 2h3"></path>
      <path d="M19 8v2a2 2 0 0 1-2 2h-3"></path>
      <line x1="12" y1="14" x2="12" y2="20"></line>
      <polyline points="9 18 12 21 15 18"></polyline>
    </svg>
  );
}

function HomeIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
      <polyline points="9 22 9 12 15 12 15 22"></polyline>
    </svg>
  );
}

function PiggyBankIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 5c-1.5 0-2.8 1.4-3 2-3.5-1.5-11-.3-11 5 0 1.8 0 3 2 4.5V20h4v-2h3v2h4v-4c1-.5 1.7-1 2-2h2v-4h-2c0-1-.5-1.5-1-2V5z"></path>
      <path d="M2 9v1c0 1.1.9 2 2 2h1"></path>
      <path d="M16 11h.01"></path>
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12"></line>
      <polyline points="12 5 19 12 12 19"></polyline>
    </svg>
  );
}

// Quote Carousel Component
const quotes = [
  {
    text: "Clariti AI focuses the part of the organisation that’s often neglected by banks, despite being such a big performance driver",
    author: "Commercial Director"
  },
  {
    text: "We are a small team and Clariti AI’s software provides us with all the insights and analytics that I had in a global bank, plus significantly more!",
    author: "Head of Product and Pricing"
  },
  {
    text: "Having spent the time to understand Clariti AI’s approach, the level of detail is extremely impressive. It would take us years to build something like this internally",
    author: "Senior Product Manager"
  },
  {
    text: "Using Clariti AI’s software for our pricing proposals has made pricing committee meetings much smoother. We face fewer challenges, and approvals are easier to obtain",
    author: "Head of Product"
  }
];

function QuoteCarousel() {
  const [activeIndex, setActiveIndex] = React.useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % quotes.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="quote-carousel">
      <div className="quote-reel-window">
        <div 
          className="quote-reel-track"
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
        >
          {quotes.map((quote, index) => (
            <div className="quote-reel-slide" key={index}>
              <div className="quote-block">
                <p className="quote-text">{quote.text}</p>
                <p className="quote-author">
                  — {quote.author}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="quote-dots">
        {quotes.map((_, index) => (
          <button
            key={index}
            className={`quote-dot ${index === activeIndex ? 'active' : ''}`}
            onClick={() => setActiveIndex(index)}
            aria-label={`Go to quote ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

export default Home;
